import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import Axios from 'axios';
import { toast } from 'sonner';

import { baseAPI } from '~shared/api/base-api';
import type { ErrorWithMessage } from '~shared/api/types/error-with-message';

export const requestForgotPasswordAPI = async (
  email: string,
  locationId: string,
) => {
  const response = await baseAPI.post<unknown>(
    `/users/request-forgot-password`,
    {
      email: email,
      locationId: locationId,
    },
  );
  return response.data;
};

/** ENR-4044: map server 404 bodies to accurate copy (not a blanket "Invalid URL"). */
export function forgotPasswordErrorMessage(error: unknown): string {
  if (!Axios.isAxiosError(error)) {
    return error instanceof Error
      ? error.message
      : 'Failed to request forgot password';
  }

  const status = error.response?.status;
  const data = error.response?.data as
    | ErrorWithMessage
    | { message?: string | string[] }
    | undefined;
  const rawMessage =
    data && 'error' in data && data.error?.message
      ? data.error.message
      : data && 'message' in data
        ? data.message
        : '';
  const serverMessage = Array.isArray(rawMessage)
    ? rawMessage.join(' ')
    : String(rawMessage ?? '');

  const normalized = serverMessage.trim().toLowerCase();

  if (status === 404) {
    if (normalized.includes('studio')) {
      return 'Studio not found. Please confirm your studio login URL and try again.';
    }
    if (normalized.includes('email') || normalized.includes('invalid')) {
      return 'No account found for that email at this studio.';
    }
    return serverMessage.trim() || 'Unable to send a one-time password.';
  }

  if (normalized.includes('otp already sent')) {
    return 'OTP already sent';
  }

  return (
    serverMessage.trim() ||
    error.message ||
    'Failed to request forgot password'
  );
}

export const useRequestForgotPassword = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({
      email,
      locationId,
    }: {
      email: string;
      locationId: string;
    }) => requestForgotPasswordAPI(email, locationId),
    onSuccess: (_data, { email, locationId }) => {
      void navigate({ to: '/forgot-password', search: { locationId, email } });
    },
    onError: (error: Error, { locationId, email }) => {
      const message = forgotPasswordErrorMessage(error);
      if (message === 'OTP already sent') {
        toast.error('OTP already sent', {
          description:
            'Please check your email for the OTP or wait to resend',
        });
        void navigate({
          to: '/forgot-password',
          search: { locationId, email },
        });
        return;
      }
      toast.error(message);
      void navigate({ to: '/login', search: { id: locationId } });
    },
  });
};
